import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { graphContainer } from '../styles';
import { brand } from 'src/themes/palette';

// Props for reusable card components
interface StatCardProps {
  title: string;
  icon: React.ReactNode;
  value: number;
  progress?: number;
  label?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  icon,
  value,
  progress,
  label,
}) => {
  const hasError = value >= 20;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6">{title}</Typography>
        <Box display="flex" alignItems="center" mt={1}>
          {icon}
          <Typography
            variant="h5"
            color={hasError ? brand.error : brand.primary}
            ml={1}>
            #{value}
          </Typography>
        </Box>
        {typeof progress === 'number' && (
          <LinearProgress
            color={hasError ? 'error' : 'secondary'}
            variant="determinate"
            value={progress}
            sx={{ mt: 2 }}
          />
        )}
        {label && (
          <Typography
            variant="caption"
            color={hasError ? brand.error : 'textSecondary'}>
            {label}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

interface ProfileStrengthCardProps {
  strength: number;
  name?: string;
  date?: string;
  radius?: number;
}

const ProfileStrengthCard: React.FC<ProfileStrengthCardProps> = ({
  strength,
  name,
  date,
  radius,
}) => (
  <Card sx={{ p: 3, width: { xs: '100%', md: '70%' } }}>
    <CardContent sx={graphContainer}>
      <Typography mt={2} variant="h6" align="center" gutterBottom>
        Profile Strength Score
      </Typography>
      <Typography variant="h6" fontWeight={'bold'} color="textSecondary">
        {name}
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        within {radius} Km - {date ? new Date(date).toLocaleString() : ''}
      </Typography>
      <Box position="relative" display="inline-flex" mx="auto">
        <CircularProgress
          variant="determinate"
          value={strength}
          size={120}
          color="secondary"
          thickness={4}
        />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center">
          <Typography variant="h6">{`${strength}%`}</Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export { StatCard, ProfileStrengthCard };
